import { Button, Card, Form, Input, message, Popconfirm, Space, Switch, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomButton from "../common/CustomButton";
import { useAuth } from "../config/AuthProvider";
import { useGoBack } from "../customHooks/useGoBack";
import { CountryCodeSelect, CurrencyCodeSelect, PaymentTermSelect, PhoneCodeSelect, PortCodeSelect } from "../helpers/common";
import OrgService from "../services/Org/OrgService";
import AppButton from "../common/AppButton";
import ConfirmDeleteButton from "../common/actions/ConfirmDeleteButton";

const { TextArea } = Input;
const { Title } = Typography;

const NewSupplier = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const mode = location?.state?.mode || "add";
  const supplier = location?.state?.supplier || {};
  const [isActive, setIsActive] = useState(false);
  const { user } = useAuth();
  const goBack = useGoBack();
  const isReadOnly = mode === "edit" && user?.role !== "ContiAdmin";
  const canEditStatus = (user?.role === "OrgAdmin" || user?.role === "User");

  useEffect(() => {
    const initializeData = () => {
      resetForm();
      if (mode === "edit" && supplier) {
        setIsActive(supplier.isActive);
        form.setFieldsValue({
          name: supplier.name,
          supplierShortCode: supplier.supplierShortCode,
          clientOrgReference: supplier.clientOrgReference,
          email: supplier.email,
          phoneCode: supplier.phoneCode,
          phoneNumber: supplier.phoneNumber,
          userReference: supplier.userReference,
          currencyCd: supplier.currencyCd,
          addressLine1: supplier.address.addressLine1,
          addressLine2: supplier.address.addressLine2,
          zipCode: supplier.address.zipCode,
          city: supplier.address.city,
          state: supplier.address.state,
          countryCd: supplier.address.countryCd,
          defaultPOL: supplier.defaultPOL,
          paymentTermsId: supplier.paymentTermsID,
          specialNote: supplier.specialNote,
          active: supplier.isActive,
        });
      }
    };
    initializeData();
  }, []);

  const resetForm = () => {
    form.resetFields();
    form.setFieldsValue({ forwarder: 1 });
  };

  const handleClose = () => {
    goBack();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const request = {
        ...values,
        isActive: isActive,
        phoneCode: values.phoneCode,
        orgType: "Supplier",
      };
      var response;
      if (mode === "edit") {
        request.id = supplier.id;
        response = await OrgService.updateOrgSupplier(request);
      } else {
        response = await OrgService.createOrgSupplier(request);
      }
      if (response?.status === 200) {
        message.success(`Supplier ${mode === "edit" ? "Updated" : "Added"} Successfully`)
        handleClose();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  const handleDelete = async () => {
    try {
      var response = await OrgService.deleteOrgSupplier(supplier?.id);
      if (response?.status === 200) {
        message.success(`Supplier Deleted Successfully`)
        handleClose();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <Form form={form} layout="vertical" className="px-8 mb-4">
      {" "}
      <div className="flex justify-start items-center w-full my-2 mx-2 font-inter">
        <span
          className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10"
        >
          {mode === "edit" ? "Supplier Details " : "New Supplier"}
        </span>
        <CustomButton
          type="link"
          onClick={goBack}
          className="text-md font-medium !text-[#01A4A6] pr-4 pt-2"
        >
          / Supplier
        </CustomButton>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 items-start">
        <div className="col-span-2 flex flex-col gap-4">
          <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80">
            <div className="mt-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="name"
                label="Company Name"
                rules={[
                  { required: true, message: "Please Enter Company Name" },
                ]}
              >
                <Input size="large" disabled={isReadOnly} />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="supplierShortCode"
                label="Short Code"
                rules={[
                  { required: true, message: "Please Enter Short Code" },
                  { max: 10, message: "Short Code must be at most 10 characters" }
                ]}
              >
                <Input size="large" maxLength={10} disabled={isReadOnly} />
              </Form.Item>
              <Form.Item name="clientOrgReference" label="Supplier Number">
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please Enter Email" },
                  { type: "email", message: "Invalid Email" },
                ]}
              >
                <Input size="large" className="!h-[34px]" disabled={isReadOnly} />
              </Form.Item>
              <Form.Item label="Phone Number" required>
                <Space.Compact className="w-full">
                  <Form.Item
                    name="phoneCode"
                    noStyle
                    rules={[{ required: true, message: "Select Phone Code" }]}
                    initialValue="61"
                  >
                    <PhoneCodeSelect selectClassNames="!w-32 !h-[34px] text-center" disabled={isReadOnly} />
                  </Form.Item>
                  <Form.Item
                    name="phoneNumber"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "Enter Phone Number",
                        min: 1,
                        max: 15,
                      },
                    ]}
                  >
                    <Input size="large" className="!h-[34px]" disabled={isReadOnly} />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </div>
          </Card>
          <Card className="pb-1 rounded-3xl shadow-xl bg-white bg-opacity-80">
            <div className="mt-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="addressLine1"
                label="Address Line 1"
                rules={[
                  {
                    required: true,
                    message: "Please Enter Suite / Floor / Building etc.",
                  },
                ]}
              >
                <TextArea size="large" rows={1} autoSize disabled={isReadOnly} />
              </Form.Item>
              <Form.Item name="addressLine2" label="Address Line 2">
                <TextArea rows={1} autoSize size="large" disabled={isReadOnly} />
              </Form.Item>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 ml-10 mr-10">
              <Form.Item
                name="zipCode"
                label="ZIP Code / Postcode"
                rules={[
                  {
                    required: true,
                    message: "Please Enter ZIP Code / Postcode",
                  },
                ]}
              >
                <Input size="large" disabled={isReadOnly} />
              </Form.Item>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: "Please Enter City" }]}
              >
                <Input size="large" disabled={isReadOnly} />
              </Form.Item>
              <Form.Item
                name="state"
                label="State / District"
                rules={[
                  { required: true, message: "Please Enter State / District" },
                ]}
              >
                <Input size="large" disabled={isReadOnly} />
              </Form.Item>
              <Form.Item
                name="countryCd"
                label="Country"
                rules={[{ required: true, message: "Please Select Country" }]}
              >
                <CountryCodeSelect disabled={isReadOnly} />
              </Form.Item>
            </div>
          </Card>
        </div>
        <Card className="pb-1 rounded-3xl shadow-xl col-span-2 bg-white bg-opacity-80">
          <div className="mt-2 grid grid-cols-2 gap-x-16 ml-10 mr-10">
            {mode === "edit" && (
              <Form.Item
                name="active"
                label="Active Status"
                valuePropName="checked"
                className="col-span-2 !w-1/2 pr-8"
                layout="horizontal"
              >
                <div className="flex justify-end items-center ">
                  <Switch
                    checked={isActive}
                    onChange={() => setIsActive(!isActive)}
                    disabled={isReadOnly && !canEditStatus}
                  />
                </div>
              </Form.Item>
            )}
            <Form.Item name="defaultPOL" label="Port of Loading">
              <PortCodeSelect disabled={isReadOnly} />
            </Form.Item>
            <Form.Item
              name="currencyCd"
              label="Currency"
              rules={[{ required: true, message: "Please Select Currency" }]}
            >
              <CurrencyCodeSelect />
            </Form.Item>
          </div>
          <div className="gap-x-16 ml-10 mr-10">
            <Form.Item name="paymentTermsId" label="Payment Terms">
              <PaymentTermSelect className="h-[34px]" />
            </Form.Item>
            <Form.Item name="specialNote" label="Notes">
              <TextArea rows={1} autoSize size="large" />
            </Form.Item>
          </div>
        </Card>
      </div>
      <div className="flex justify-end mr-8">
        <AppButton variant="save" onClick={handleSubmit}>Save</AppButton>
        {mode === "edit" ? (
            <ConfirmDeleteButton title={
              <>
                You are about to delete this Supplier.
                <br />
                Please confirm you wish to proceed.
              </>
            }
            onConfirm={handleDelete}/>
        ) : null}
        <AppButton variant="close" onClick={handleClose}>Close</AppButton>
      </div>
    </Form>
  );
};

export default NewSupplier;
